import api from './api';
import { handleApiError } from './apiError';
import { OrgDetails, type PSPrice } from './eventsApi';

/**
 * ── bookingsApi — creating a booking, and reading one back ───────────────
 *
 * Three calls. The first two are the whole payment flow's server side:
 *
 *   `createBooking`  POST /bookings      — holds the seats, opens the Razorpay order
 *   `getBooking`     GET  /bookings/{id} — the poll that tells us the money landed
 *
 * and the third is the reader's own history:
 *
 *   `listReservations` GET /me/reservations — every booking this user holds
 *
 * There is NO "create payment" call between them: `POST /bookings` validates the
 * lines, reserves the seats, creates the booking AND opens the Checkout order in
 * one shot, handing back a `payment` block that IS the argument object for the
 * Razorpay SDK. And there's no "confirm" call after them: the booking is
 * confirmed server-side when Razorpay's capture webhook lands, so the client's
 * only way to learn the outcome is to keep asking. The sheet closing means
 * nothing on its own — see [[usePollBooking]].
 *
 * Casing is not this module's job — [[api]] converts both directions — so the
 * request bodies below are written camelCase (`slotTier` goes out as
 * `slot_tier`) and the `Body` types are the wire shape ALREADY camelCased, and
 * a CLAIM about it that nothing validates.
 *
 * ── The two return types are deliberately different sizes ───────────────
 * `createBooking` returns a `CreatedBooking`: an id, a status, and the payment
 * block. That's everything the confirm screen does with it — route to the
 * booking, and open the payment sheet. It does NOT return the full receipt,
 * because the caller is standing in front of an event it already fetched, and a
 * booking it's about to navigate away from.
 *
 * `getBooking` returns the whole `Booking`, because its caller (the booking
 * screen) draws a receipt: the event's identity, the slot, the lines, the money.
 *
 * `listReservations` returns a `Reservation[]` — `Booking` minus the money, which
 * is the server's choice rather than ours: the list serializer simply doesn't send
 * it. See that type.
 * ───────────────────────────────────────────────────────────────────────
 */

/**
 * Where a booking can be. `pending_payment` is the only NON-terminal one — it's
 * what the poll waits to leave — and `confirmed` is the only happy end; the
 * others are all ways for it to be over without a ticket.
 */
export type BookingStatus =
  | 'pending_payment'
  | 'confirmed'
  | 'cancelled'
  | 'expired'
  | 'refunded'
  | 'partially_refunded';

/**
 * The Razorpay Checkout order, exactly as the SDK's `open()` wants it (bar the
 * casing this layer restores at the call site). Not a link and not something the
 * app assembles — the server created the order and this describes it.
 */
export type BookingPayment = {
  /** The publishable key id (`rzp_test_…`), not a secret. */
  key: string;
  orderId: string;
  /**
   * PAISE, as a bare integer. Deliberately not the money object the rest of the
   * response uses — this is what the SDK takes.
   */
  amount: number;
  currency: string;
  /** The merchant/event name the payment sheet prints. */
  name: string;
  /** The payer's details, so the sheet doesn't ask them to retype. */
  prefill: { name: string; email: string; contact: string };
};

/** What `POST /bookings` gives back — see the module docstring on the two sizes. */
export type CreatedBooking = {
  id: string;
  status: BookingStatus;
  /**
   * The Checkout order to open the SDK with — present only while money is still
   * owed. It's attached to an uncaptured payment, so its ABSENCE is the
   * cleanest "this is paid for" signal the client has, and a free booking never
   * has one at all.
   */
  payment: BookingPayment | null;
};

/**
 * The event a booking is against, as the booking carries it: only the fields the
 * ticket screen prints, not the whole `Event`. Just one image — the receipt
 * shows a single thumbnail.
 */
export type BookingEvent = {
  id: string;
  title: string;
  images: { id: string; url: string }[];
  /**
   * PLURAL, the same spelling [[eventsApi]]'s `Event` uses — one word for this
   * field across the whole app.
   *
   * The booking serializers don't send it under that name today, so this one is
   * FABRICATED on the way in; see the terms shim at the bottom of the file. Above
   * that shim nothing needs to know the wire disagrees.
   */
  termsAndConditions: string;
  venue: { name: string; googleMapsUrl: string };
  /** The event's go-to human. `email` is optional; the phone always isn't. */
  support: { name: string; mobile: string; email?: string };
  orgDetails: OrgDetails | null;
};

/** The booked slot, frozen onto the booking. */
export type BookingSlot = {
  id: string;
  startDatetime: string;
  endDatetime: string;
};

/** One booked tier: what it was, how many, and what that came to. */
export type BookingTicket = {
  ticketTierId: string;
  slotTierId: string;
  quantity: number;
  unitPrice: PSPrice;
  lineTotal: PSPrice;
  ticketTierName: string;
};

/**
 * A booking, whole — what `GET /bookings/{id}` returns and the ticket screen
 * renders. Everything the screen needs is IN here (the event and slot are
 * nested, not ids to go and resolve), which is what makes that screen a single
 * fetch.
 *
 * `amountDue` is only meaningful while `status` is `pending_payment` or
 * `confirmed`: once a payment row exists it echoes that payment's amount and
 * stops tracking the lines, so on a cancelled booking it reads as money owed
 * against `subtotal: ₹0`. Don't print it as "due" outside those two states.
 */
export type Booking = {
  id: string;
  status: BookingStatus;
  event: BookingEvent;
  slot: BookingSlot;
  tickets: BookingTicket[];
  subtotal: PSPrice;
  discount: PSPrice;
  amountDue: PSPrice;
  total: PSPrice;
  /** The seat hold's deadline — null once there's nothing left to hold. */
  reservationExpiresAt: string | null;
  createdAt: string;
  /** As on `CreatedBooking`: present only while the money is still owed. */
  payment: BookingPayment | null;
};

/**
 * One row of `GET /me/reservations`.
 *
 * Derived from `Booking` rather than spelled out again because it IS `Booking` —
 * the list endpoint runs the same nested serializer (same `event`, same `slot`,
 * same `tickets`, right down to the same misspelled terms key), and simply omits
 * the four money-and-hold fields. Checked field-for-field against staging on
 * 2026-08-12 by reading one id out of this list and fetching it through
 * `getBooking`.
 *
 * Writing it as an `Omit` is the point: the day a field lands on the booking
 * serializer it lands here too, and a `Reservation` that had been typed by hand
 * would quietly disagree. The five that are gone are gone for a reason —
 * `subtotal` / `discount` / `amountDue` / `reservationExpiresAt` / `payment` all describe a
 * TRANSACTION, and a list of things you hold isn't one; the card the profile
 * screen draws shows no money at all.
 *
 * So this is not "a lighter Booking to save bytes" — don't reach for it as one.
 * Anything that needs the money must fetch the booking (`getBooking`), and the
 * absence of `payment` here specifically means this list cannot tell you whether
 * a reservation is still awaiting one.
 *
 * `status` rides along even though no card renders it — see `listReservations` on
 * what that leaves open.
 */
export type MinimalBooking = Omit<
  Booking,
  'subtotal' | 'discount' | 'amountDue' | 'reservationExpiresAt' | 'payment'
>;

/** One line of a new booking: a slot×tier allocation, and how many seats of it. */
export type NewBookingItem = {
  /** An `EventSlotTicketTier.slotTierId` — NOT `ticketTierId`, which 400s. */
  slotTierId: string;
  quantity: number;
};

/**
 * Create the booking. 🔒 Authenticated, and the one WRITE in the flow.
 *
 * A 201 comes back either `pending_payment` with a `payment` block (the money
 * path) or, when the total is zero, `confirmed` with none — so the caller
 * doesn't have to know which it asked for. Everything else throws an
 * [[ApiError]] whose `message` is the server's own words and is meant to be
 * shown: a sold-out tier, not enough seats, a per-user cap (409), a closed slot
 * (400), an incomplete profile (400 `precondition_error`), a membership-gated
 * tier (403).
 *
 * Seats are held for FIVE MINUTES from here. Nothing to send to keep them — it's
 * set once and never extended.
 *
 * ── The idempotency key is the CALLER's ─────────────────────────────────
 * It's a required argument rather than something generated in here, because
 * what the key protects is a decision this module can't see: the user pressing
 * "Pay now" ONCE. A key minted per call would be a fresh key per retry, which is
 * exactly the double-booking the header exists to prevent — so the key has to
 * outlive the call, and its owner is the screen (see the confirm step, which
 * mints one per visit and per coupon change with [[newIdempotencyKey]]).
 *
 * With it, a retry behind a flaky network replays the ORIGINAL booking instead
 * of creating a second one — the case where the request reached the server and
 * only the response was lost.
 *
 * `couponCode` must be the one `previewBooking` priced — nothing carries over
 * from the preview, so omitting it charges full price after showing a discount.
 */
async function createBooking(input: {
  eventId: string;
  items: NewBookingItem[];
  couponCode?: string;
  idempotencyKey: string;
}): Promise<CreatedBooking> {
  const response = await api.post<CreateBookingBody>(
    '/bookings',
    {
      // `event`, not `eventId` — the wire's spelling. The items are remapped for
      // the same reason: the app calls it `slotTierId`, the API calls it
      // `slot_tier`, and translating between the two is this layer's whole job.
      event: input.eventId,
      items: toWireItems(input.items),
      couponCode: input.couponCode,
    },
    { headers: { 'Idempotency-Key': input.idempotencyKey } }
  );

  if (!response.ok || !response.data) handleApiError(response);

  const body = response.data;
  return {
    id: body.id,
    status: body.status,
    payment: body.payment ?? null,
  };
}

/** What `POST /bookings/preview` prices the cart at. */
export type BookingPreview = {
  /** Echoed back when the code applied; null when none was sent. */
  couponCode: string | null;
  subtotal: PSPrice;
  discount: PSPrice;
  amountDue: PSPrice;
};

/**
 * Price the cart, optionally with a coupon, without writing anything. 🔒
 * A bad coupon fails the whole call (404 unknown, 400 expired/not yet active,
 * 409 redemption cap) with a message meant for the user.
 */
async function previewBooking(input: {
  eventId: string;
  items: NewBookingItem[];
  couponCode?: string;
}): Promise<BookingPreview> {
  const response = await api.post<BookingPreview>('/bookings/preview', {
    event: input.eventId,
    items: toWireItems(input.items),
    couponCode: input.couponCode,
  });

  if (!response.ok || !response.data) handleApiError(response);

  return response.data;
}

function toWireItems(items: NewBookingItem[]) {
  return items.map(({ slotTierId, quantity }) => ({
    slotTier: slotTierId,
    quantity,
  }));
}

/**
 * Read a booking back, whole. 🔒 Authenticated, and scoped to the caller —
 * someone else's booking id is a 404, never a 403.
 *
 * This is the poll AND the ticket screen's fetch; there's nothing else. See
 * [[usePollBooking]] for the loop that drives it after payment.
 *
 * One request, and one `response.data`. This used to stitch the nested shape
 * together from a flat response plus a second `/events/{id}` fetch; that shim is
 * gone, because the nested response landed.
 */
async function getBooking(id: string): Promise<Booking> {
  const response = await api.get<Booking>(`/me/bookings/${id}`);

  if (!response.ok || !response.data) handleApiError(response);

  return response.data;
}

/**
 * Every booking this user holds, newest FIRST. 🔒 Authenticated, and scoped to
 * the caller by the token alone — there's no id to pass and no way to ask for
 * someone else's.
 *
 * This is the profile screen's list (see [[useFetchBookings]]), and it is the ONE
 * call that answers "what have I booked" — `getBooking` needs an id, so without
 * this there's no way to discover one.
 *
 * ── Two things it deliberately does not do ──────────────────────────────
 *
 * It does not FILTER. Every reservation the server returns comes back, whatever
 * its `status` — so a `cancelled` or `expired` one is in here, and today's card
 * draws it identically to a live booking. That's the server's list to scope and
 * the design's state to draw; inventing a client-side filter here would hide rows
 * nobody decided to hide, and pick the wrong ones the first time `status` grows a
 * value. The field is on `Reservation` for whenever that state gets designed.
 *
 * It does not PAGE. The response is a cursor envelope — `{data, page:{cursor,
 * nextCursor, hasMore}}` — and only the first page is read; `nextCursor` is
 * dropped on the floor. One page is every reservation a real user has today and
 * the profile list has no scroll-to-load, so following the cursor would be a loop
 * written for nobody. It IS a cap though, so it says so out loud in the log rather
 * than silently truncating — the day that line appears, the fix is to thread
 * `cursor` through here and let the caller ask for more.
 */
async function listBookings(): Promise<MinimalBooking[]> {
  const response = await api.get<ReservationsBody>('/me/bookings');

  if (!response.ok || !response.data) handleApiError(response);

  const { data, page } = response.data;

  if (page?.hasMore) {
    // Not an error — see the docstring. Loud on purpose: this is the only sign
    // that the screen is showing a prefix of the user's reservations.
    // TODO: add pagination here
  }

  return data;
}

// The narrow create response. `payment` is omitted by the server rather than
// nulled, which is the one thing the mapping above normalises.
type CreateBookingBody = {
  id: string;
  status: BookingStatus;
  payment?: BookingPayment;
};

/**
 * The cursor envelope `/me/reservations` wraps its page in. Unique to this
 * endpoint so far — `/home` and `/events/{id}` return bare bodies — so it's typed
 * here rather than shared.
 *
 * `cursor` is the one this page was fetched WITH (null for the first), and
 * `nextCursor` the one the following page wants; `hasMore` saves comparing it to
 * null. Optional on the type because a `page` block the server stopped sending
 * must not crash the list — `page?.hasMore` reads false and the rows still render.
 */
type ReservationsBody = {
  data: MinimalBooking[];
  page?: {
    cursor: string | null;
    nextCursor: string | null;
    hasMore: boolean;
  };
};

// ── SHIM: the misspelled terms key ───────────────────────────────────────────
//

// collapse back into `Booking` / `Reservation`, `getBooking` and
// `listReservations` return `response.data` untouched, and no screen changes.
//
// The booking serializers — both `GET /bookings/{id}` and `GET /me/reservations`
// — send the nested event's terms under the SINGULAR `terms_and_condition`, while
// `GET /events/{id}` sends the same field as `terms_and_conditions`. Two
// spellings for one field is not something to propagate upward, so `BookingEvent`
// declares the plural (matching [[eventsApi]]) and the rename happens here.
//
// ── Why it reads BOTH keys ──
// So the fix needs no client deploy to land, and no client deploy to survive.
// Whichever spelling arrives is the one used: today the singular, after the
// rename the plural, and during a partial rollout whichever that server sent. If
// this only read the singular it would break on the very fix it's waiting for —
// which is the failure mode that put empty terms on the booking screen in the
// first place, since nothing validates these types against the wire (see api.ts).

/**
 * A booking's nested event as it actually ARRIVES: either spelling of the terms
 * key, and neither guaranteed. Both optional because the client can't know which
 * server it's talking to — the whole point of the fallback below.
 */

/** `Booking` / `Reservation` as they arrive — same, bar the event's terms key. */

/**
 * Rename the terms key on the way in. Generic over the body so ONE function
 * serves the single booking and each row of the list (`data.map(withTerms)`) —
 * everything else on the body passes through untouched.
 */
// ── end of terms shim ────────────────────────────────────────────────────────

export default {
  createBooking,
  previewBooking,
  getBooking,
  listBookings,
};
